// App Context - Manages global state for Forward Command
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { toast } from "react-toastify";
import * as api from "./supabase";
import { getForemanAssignments } from "./supabase";
import { useAuth } from "./AuthContext";
import { useOfflineQueue } from "../hooks/useOfflineQueue";

const AppContext = createContext(null);

export function AppProvider({ children }) {
  const { employee, organizations, signOut } = useAuth();

  // Offline queue for resilient data sync
  const offlineQueue = useOfflineQueue();

  // Get org from authenticated user
  const primaryOrg =
    organizations.find((o) => o.is_primary) || organizations[0];
  const orgId = primaryOrg?.org_id || null;
  const organization = primaryOrg?.organization || null;

  // Current user from auth
  const currentUser = employee
    ? {
        id: employee.id,
        name:
          employee.display_name ||
          `${employee.first_name} ${employee.last_name}`,
        role: employee.role || "user",
        employee_type: employee.employee_type || "employee",
        initials: (
          (employee.first_name?.[0] || "") + (employee.last_name?.[0] || "")
        ).toUpperCase(),
      }
    : null;

  // Location
  const [location, setLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);

  // Jobs
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobsLoading, setJobsLoading] = useState(true);
  const [jobsError, setJobsError] = useState(null);

  // Crew & Time
  const [crew, setCrew] = useState([]);
  const [crewLoading, setCrewLoading] = useState(false);
  const [crewError, setCrewError] = useState(null);

  // Cost Codes
  const [costCodes, setCostCodes] = useState([]);

  // Vocabulary (custom terms for voice commands)
  const [vocabulary, setVocabulary] = useState([]);

  // UI State - start with null view until we determine routing
  const [currentView, setCurrentView] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [initialRouteChecked, setInitialRouteChecked] = useState(false);

  // Pending voice data to pass between screens - use ref to persist across view changes
  const pendingVoiceRef = useRef(null);
  const [pendingVoiceAllocation, setPendingVoiceAllocation] = useState(null);
  // Force re-render counter to ensure state propagates
  const [, forceUpdate] = useState(0);

  // Sync ref to state for reactivity
  const setPendingVoice = (data) => {
    pendingVoiceRef.current = data;
    setPendingVoiceAllocation(data);
  };

  // Get pending voice - returns ref value (always current) or state value
  const getPendingVoice = () => pendingVoiceRef.current;

  // Request location - called on user gesture for iOS compatibility
  const requestLocation = useCallback(() => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        (error) => {
          console.warn("Geolocation error:", error);
          setLocationError(error.message);
          setLocation({ latitude: 0, longitude: 0 });
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 }, // Cache for 5 min
      );
    } else {
      setLocationError("Geolocation not supported");
      setLocation({ latitude: 0, longitude: 0 });
    }
  }, []);

  // Check if we already have location permission and get location if so
  useEffect(() => {
    setLocation({ latitude: 0, longitude: 0 });

    // Check permission status (doesn't trigger prompt)
    if (navigator.permissions && navigator.permissions.query) {
      navigator.permissions
        .query({ name: "geolocation" })
        .then((result) => {
          if (result.state === "granted") {
            // Already have permission - safe to request without user gesture
            requestLocation();
          }
          // If "prompt" or "denied", wait for user gesture
        })
        .catch(() => {
          // permissions API not supported, that's ok
        });
    }
  }, [requestLocation]);

  // ============================================
  // FORWARD COMMAND ROUTING LOGIC
  // ============================================
  // On app start, check foreman's assignments for today:
  // - If exactly 1 active assignment → auto-select and go to Command Home
  // - If 0 or multiple → show Job Picker
  // No confirmation screens. No "Are you sure?" Deterministic routing.
  useEffect(() => {
    if (initialRouteChecked || !employee?.id || !orgId) return;

    const checkAssignmentsAndRoute = async () => {
      try {
        const assignments = await getForemanAssignments(orgId, employee.id);

        if (assignments.length === 1) {
          // Single assignment → auto-select and go straight to Command Home
          setSelectedJob(assignments[0]);
          setCurrentView("command");
        } else {
          // 0 or multiple assignments → show job picker
          setCurrentView("jobs");
        }
      } catch (error) {
        console.error("Forward Command: Error checking assignments", error);
        // On error, fall back to job picker
        setCurrentView("jobs");
      } finally {
        setInitialRouteChecked(true);
      }
    };

    checkAssignmentsAndRoute();
  }, [employee?.id, orgId, initialRouteChecked]);

  // Load jobs from Supabase
  const loadJobs = useCallback(async () => {
    setJobsLoading(true);
    setJobsError(null);
    try {
      const data = await api.getActiveJobs(orgId);

      // Sort by distance if we have real location
      if (location && location.latitude !== 0) {
        const sorted = api.sortJobsByDistance(
          data,
          location.latitude,
          location.longitude,
        );
        setJobs(
          sorted.map((job) => ({
            ...job,
            // Jobs without lat/long get distance = Infinity from sortJobsByDistance;
            // never render that as a fake distance.
            distanceText: !Number.isFinite(job.distance)
              ? "--"
              : job.distance < 0.5
                ? "At Site"
                : job.distance < 10
                  ? `${job.distance.toFixed(1)} mi`
                  : `${Math.round(job.distance)} mi`,
            isClosest: Number.isFinite(job.distance) && job.distance < 0.5,
          })),
        );
      } else {
        // No location - just show jobs without distance
        setJobs(
          data.map((job) => ({
            ...job,
            distanceText: "--",
            isClosest: false,
          })),
        );
      }
    } catch (error) {
      console.error("Error loading jobs:", error);
      setJobsError(error.message);
      setJobs([]);
    } finally {
      setJobsLoading(false);
    }
  }, [orgId, location]);

  useEffect(() => {
    if (location) {
      loadJobs();
    }
  }, [location, loadJobs]);

  // Load crew from Supabase when job is selected
  const loadCrew = useCallback(async () => {
    if (!selectedJob) {
      setCrew([]);
      return;
    }

    setCrewLoading(true);
    setCrewError(null);
    try {
      // Get work date in job's timezone
      const workDate = new Date().toLocaleDateString("en-CA", {
        timeZone: selectedJob.timezone || "America/Denver",
      });
      const data = await api.getTodaysCrew(
        selectedJob.id,
        workDate,
        selectedJob.timezone,
      );
      const mappedCrew = data.map((member) => ({
        ...member,
        color: member.avatar_color || "#64748b",
      }));
      setCrew(mappedCrew);
    } catch (error) {
      console.error("Error loading crew:", error);
      setCrewError(error.message);
      setCrew([]);
    } finally {
      setCrewLoading(false);
    }
  }, [selectedJob]);

  useEffect(() => {
    loadCrew();
  }, [loadCrew]);

  // Load cost codes from Supabase when job is selected
  const loadCostCodes = useCallback(async () => {
    if (!selectedJob) {
      setCostCodes([]);
      return;
    }

    try {
      const data = await api.getJobCostCodes(selectedJob.id);
      setCostCodes(data);
    } catch (error) {
      console.error("Error loading cost codes:", error);
      setCostCodes([]);
    }
  }, [selectedJob]);

  useEffect(() => {
    loadCostCodes();
  }, [loadCostCodes]);

  // Load vocabulary for voice commands
  const loadVocabulary = useCallback(async () => {
    if (!orgId) {
      setVocabulary([]);
      return;
    }

    try {
      const data = await api.loadVocabulary(orgId);
      setVocabulary(data);
    } catch (error) {
      console.error("Error loading vocabulary:", error);
      setVocabulary([]);
    }
  }, [orgId]);

  useEffect(() => {
    loadVocabulary();
  }, [loadVocabulary]);

  // Clock in a crew member
  const clockInMember = async (memberId, photoUrl = null) => {
    try {
      await api.clockIn({
        orgId,
        employeeId: memberId,
        jobId: selectedJob.id,
        jobTimezone: selectedJob.timezone, // Use job's timezone for work_date
        latitude: location?.latitude,
        longitude: location?.longitude,
        photoUrl,
        enteredBy: currentUser?.id || null,
      });
      await loadCrew(); // Refresh from database
    } catch (error) {
      console.error("Clock in error:", error);
      throw error;
    }
  };

  // Clock out a crew member
  const clockOutMember = async (memberId, photoUrl = null) => {
    try {
      const member = crew.find((m) => m.id === memberId);
      if (!member?.timeEntry?.id) {
        throw new Error("No active time entry found");
      }
      await api.clockOut(member.timeEntry.id, {
        latitude: location?.latitude,
        longitude: location?.longitude,
        photoUrl,
      });
      await loadCrew(); // Refresh from database
    } catch (error) {
      console.error("Clock out error:", error);
      throw error;
    }
  };

  // Bulk clock in all crew not yet clocked in
  const bulkClockIn = async () => {
    const notClockedIn = crew.filter((m) => !m.clockIn);
    if (notClockedIn.length === 0) {
      return;
    }

    try {
      await api.bulkClockIn({
        orgId,
        employeeIds: notClockedIn.map((m) => m.id),
        jobId: selectedJob?.id,
        jobTimezone: selectedJob?.timezone, // Use job's timezone for work_date
        enteredBy: currentUser?.id || null,
      });
      await loadCrew(); // Refresh from database
    } catch (error) {
      console.error("Bulk clock in error:", error?.message || error);
      toast.error("Clock in failed: " + (error?.message || "Unknown error"));
    }
  };

  // Bulk clock out all crew currently clocked in
  const bulkClockOut = async () => {
    const clockedIn = crew.filter((m) => m.clockIn && !m.clockOut);
    if (clockedIn.length === 0) return;

    try {
      const entryIds = clockedIn
        .filter((m) => m.timeEntry?.id)
        .map((m) => m.timeEntry.id);
      if (entryIds.length > 0) {
        await api.bulkClockOut(entryIds);
      }
      await loadCrew(); // Refresh from database
    } catch (error) {
      console.error("Bulk clock out error:", error);
      throw error;
    }
  };

  // Update time entry (edit clock in/out times)
  const updateTime = async (
    memberId,
    { clockIn, clockOut, editSource = "manual" },
  ) => {
    const member = crew.find((m) => m.id === memberId);
    if (!member?.timeEntry?.id) {
      throw new Error("No time entry found for member");
    }

    try {
      await api.updateTimeEntry(member.timeEntry.id, {
        clockIn,
        clockOut,
        jobTimezone: selectedJob?.timezone || "America/Denver",
        editedBy: currentUser?.id || null,
        editSource,
      });
      await loadCrew(); // Refresh from database
    } catch (error) {
      console.error("Update time error:", error);
      throw error;
    }
  };

  // Save allocations for a crew member
  const saveAllocations = async (memberId, allocations) => {
    const member = crew.find((m) => m.id === memberId);
    if (!member?.timeEntry?.id) {
      throw new Error("No time entry found for member");
    }

    try {
      await api.saveAllocations(
        member.timeEntry.id,
        allocations,
        currentUser?.id || null,
      );
      await loadCrew(); // Refresh from database
    } catch (error) {
      console.error("Save allocations error:", error);
      throw error;
    }
  };

  // Upload a photo
  const uploadPhoto = async (file, category = "progress", description = "") => {
    try {
      const timestamp = Date.now();
      const path = `${orgId}/${selectedJob.id}/${timestamp}.jpg`;
      const photoUrl = await api.uploadPhoto(file, path);

      await api.saveJobPhoto({
        orgId,
        jobId: selectedJob.id,
        takenBy: currentUser?.id || null,
        photoUrl,
        category,
        description,
        latitude: location?.latitude,
        longitude: location?.longitude,
      });

      return photoUrl;
    } catch (error) {
      console.error("Photo upload error:", error);
      throw error;
    }
  };

  // Navigation - supports passing data to target screen
  const navigate = (view, data = null) => {
    // If navigating to time allocation with voice data, store it
    if (view === "time" && data?.voiceAllocation) {
      // Add unique ID to prevent double-processing in React Strict Mode
      const voiceDataWithId = {
        ...data.voiceAllocation,
        _id:
          data.voiceAllocation._id ||
          `voice_${Date.now()}_${Math.random().toString(36).slice(2)}`,
      };
      // Set ref FIRST (synchronous, immediately available)
      pendingVoiceRef.current = voiceDataWithId;
      // Then set state (triggers re-render with new value)
      setPendingVoiceAllocation(voiceDataWithId);
      // Change view in same tick - ref is already set so component can read it
      setCurrentView(view);
      setMenuOpen(false);
    } else {
      setCurrentView(view);
      setMenuOpen(false);
    }
  };

  // Clear pending voice allocation (called after it's consumed)
  const clearPendingVoiceAllocation = () => {
    setPendingVoice(null);
  };

  const selectJob = (job) => {
    setSelectedJob(job);
    navigate("command");
  };

  const value = {
    // State
    orgId,
    organization,
    currentUser,
    location,
    locationError,
    jobs,
    jobsLoading,
    jobsError,
    selectedJob,
    crew,
    crewLoading,
    crewError,
    costCodes,
    vocabulary,
    currentView,
    menuOpen,

    // Actions
    requestLocation,
    loadJobs,
    loadCrew,
    selectJob,
    clockInMember,
    clockOutMember,
    bulkClockIn,
    bulkClockOut,
    updateTime,
    saveAllocations,
    uploadPhoto,
    navigate,
    setMenuOpen,
    setSelectedJob,
    signOut,
    // Voice data passing
    pendingVoiceAllocation,
    getPendingVoice,
    clearPendingVoiceAllocation,

    // Offline queue
    offlineQueue,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useApp must be used within an AppProvider");
  }
  return context;
}

export default AppContext;
